import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faDeleteLeft,
  faEye,
  faPencil,
  faTrash,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";
import { Container } from "components";
import Pagination from "components/pagination";
import { useToaster } from "hooks";
import moment from "moment";
import api from "services/api";

export const PromocodesList = () => {
  const [promocodes, setPromocodes] = useState({ data: [], pagination: {} });
  const [currentPage, setCurrentPage] = useState(1);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [deleteModal, setDeleteModal] = useState(false);
  const [selectedPromocodeId, setSelectedPromocodeId] = useState(null);
  const { setErrorToaster, setSuccessToaster } = useToaster();

  const fetchPromocodes = async (page = 1, query = "", status = "") => {
    try {
      setLoading(true);
      let url = `/promocodes?page=${page}`;
      if (query) url += `&search=${query}`;
      if (status) url += `&status=${status}`;
      
      const response = await api.get(url);
      // The API returns data in response.data.data array (same as partners)
      setPromocodes(response?.data || { data: [], pagination: {} });
      setCurrentPage(page);
    } catch (error) {
      console.log("fetchPromocodes errors:", error);
      setErrorToaster("Failed to fetch promocodes");
      setPromocodes({ data: [], pagination: {} });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPromocodes(1, search, statusFilter);
  }, []);

  const openDeleteModal = (id) => {
    setSelectedPromocodeId(id);
    setDeleteModal(true);
  };

  const handleDeleteModelConfirmation = async () => {
    if (selectedPromocodeId) {
      try {
        const response = await api.delete(`/promocodes/${selectedPromocodeId}`);
        console.log("handleDeleteModelConfirmation:", response);
        setDeleteModal(false);
        setSelectedPromocodeId(null);
        fetchPromocodes(currentPage, search, statusFilter);
        setSuccessToaster("Successfully deleted promocode");
      } catch (error) {
        console.log("handleDeleteModelConfirmation Error:", error);
        setErrorToaster(error?.response?.data?.message || "Failed to delete promocode");
      }
    }
  };

  const handleSearch = () => {
    fetchPromocodes(1, search, statusFilter);
  };

  const handleStatusFilter = (status) => {
    setStatusFilter(status);
    fetchPromocodes(1, search, status);
  };

  const getStatusBadge = (status) => {
    const statusClasses = {
      Active: "bg-success",
      Pause: "bg-warning", 
      Stop: "bg-danger"
    };
    return statusClasses[status] || "bg-secondary";
  };


  return (
    <Container>
      <div className="content-wrapper">
        <div
          className="container-xxl flex-grow-1 container-p-y"
          style={{ height: "43px" }}
        >
          <h4 className="card-header">Promocodes</h4>
          <div className="d-flex justify-content-between align-items-center mb-4">
            <div className="d-flex">
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearch();
                }}
                placeholder="Search promocodes..."
                className="form-control me-3"
                style={{
                  outline: "none",
                  boxShadow: "none",
                  border: "1px solid #d9dee3",
                  width: "300px"
                }}
              />
              <button onClick={handleSearch} className="btn btn-outline-primary me-3">
                Search
              </button>
              <select
                value={statusFilter}
                onChange={(e) => handleStatusFilter(e.target.value)}
                className="form-select me-3"
                style={{ width: "150px" }}
              >
                <option value="">All Status</option>
                <option value="Active">Active</option>
                <option value="Pause">Pause</option>
                <option value="Stop">Stop</option>
              </select>
            </div>
            <button
              className="btn btn-primary"
              onClick={() => navigate("/addPromocode")}
            >
              <FontAwesomeIcon icon={faPlus} className="me-2" />
              Add Promocode
            </button>
          </div>
          <div className="card-datatable table-responsive">
            <table className="invoice-list-table table border-top dataTable no-footer dtr-column mb-5">
              <thead>
                <tr>
                  <th>Sno</th>
                  <th>Promocode</th>
                  <th>Percentage</th>
                  <th>Expiry Date</th>
                  <th>Status</th>
                  <th>Created By</th>
                  <th>Created On</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="8" className="text-center">
                      <div className="spinner-border" role="status">
                        <span className="visually-hidden">Loading...</span>
                      </div>
                    </td>
                  </tr>
                ) : promocodes?.data && promocodes?.data?.length > 0 ? (
                  promocodes?.data?.map((promocode, index) => {
                    const serialNumber =
                      (currentPage - 1) * promocodes?.pagination?.per_page +
                      (index + 1);
                    return (
                      <tr key={promocode.id}>
                        <td>{serialNumber}</td>
                        <td>
                          <strong>{promocode.promocode}</strong>
                        </td>
                        <td>{promocode.coupon_percentage}%</td>
                        <td>
                          {moment(promocode.expiry_date).format("DD-MM-YYYY")}
                        </td>
                        <td>
                          <span className={`badge ${getStatusBadge(promocode.status)}`}>
                            {promocode.status}
                          </span>
                        </td>
                        <td>{promocode.creator?.name || "N/A"}</td>
                        <td>
                          {moment(promocode.created_at).format("DD-MM-YYYY")}
                        </td>
                        <td>
                          <FontAwesomeIcon
                            icon={faEye}
                            className="me-3 text-primary"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              navigate(`/promocodeDetails/${promocode.id}`)
                            }
                            title="View Details"
                          />
                          <FontAwesomeIcon
                            title="Edit"
                            icon={faPencil}
                            className="me-3 text-warning"
                            style={{ cursor: "pointer" }}
                            onClick={() =>
                              navigate(`/editPromocode/${promocode.id}`)
                            }
                          />
                          <FontAwesomeIcon
                            icon={faTrash}
                            className="text-danger"
                            style={{ cursor: "pointer" }}
                            onClick={() => {
                              openDeleteModal(promocode.id);
                            }}
                            title="Delete"
                          />
                        </td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="8" className="text-center">
                      <strong>No promocodes found</strong>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
            {promocodes?.pagination?.total > 0 && (
              <Pagination
                totalPages={promocodes?.pagination?.total}
                perPage={promocodes?.pagination?.per_page}
                handleFunction={(page) => fetchPromocodes(page, search, statusFilter)}
              />
            )}
          </div>
        </div>
      </div>
      {deleteModal && (
        <div className="modal show d-block" tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-exclamation-triangle text-warning me-2"></i>
                  Delete Promocode
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={() => {
                    setDeleteModal(false);
                    setSelectedPromocodeId(null);
                  }}
                ></button>
              </div>
              <div className="modal-body">
                <p>Are you sure you want to delete this promocode?</p>
                <p className="text-muted small">
                  This action cannot be undone. The promocode will be permanently removed from the system.
                </p>
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setDeleteModal(false);
                    setSelectedPromocodeId(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  className="btn btn-danger"
                  onClick={handleDeleteModelConfirmation}
                >
                  <i className="fas fa-trash me-1"></i>
                  Delete Promocode
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </Container>
  );
};
